import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { MedplumProvider } from '@medplum/react'
import { SeenMedplumClient } from '@seen/core/client'
import {
  Loader,
  QueryProvider,
  SeenApiContextProvider,
  SeenMantineTheme,
} from '@seen/react'
import {
  type JSX,
  StrictMode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { RouterProvider } from 'react-router'
import { App } from '#src/App.tsx'
import {
  APP_COMMIT_SHA,
  APP_VERSION,
  IS_DEV,
  MEDPLUM_BASE_URL,
  SEEN_API_URL,
} from '#src/config.ts'
import { SignInPage } from '#src/pages/SignInPage.tsx'

const search = new URLSearchParams(window.location.search)
const traceId = search.get('DEBUG_TRACE_ID') ?? undefined

// Initialize meal-ticket-printer

const medplum = new SeenMedplumClient({
  traceId,
  baseUrl: MEDPLUM_BASE_URL,
  onUnauthenticated: () => {
    const authContext = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      online: navigator.onLine,
      memory_token_exists: !!medplum.getAccessToken(),
      base_url: MEDPLUM_BASE_URL,
      activeLogin_exists: !!localStorage.getItem('activeLogin'),
      logins_exists: !!localStorage.getItem('logins'),
    }
    console.error('Authentication lost - Context:', authContext)
  },
  cacheTime: 1000,
  autoBatchTime: 0,
})

export function Root(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true)
  const isInitializedRef = useRef(false)

  const router = useMemo(
    () =>
      createBrowserRouter(
        [
          {
            path: '/',
            element: <App />,
          },
          {
            path: '/signin',
            element: <SignInPage />,
          },
        ],
        {}
      ),
    []
  )
  const navigate = (path: string): Promise<void> => router.navigate(path)

  useEffect(() => {
    if (isInitializedRef.current) {
      return
    }
    isInitializedRef.current = true

    // oxlint-disable-next-line no-console -- debug logging
    console.log('Root: Starting getProfileAsync...')

    void medplum
      .getProfileAsync()
      .then((profile) => {
        // oxlint-disable-next-line no-console -- debug logging
        console.log('Root: getProfileAsync resolved with:', profile)
        setIsLoading(false)
      })
      .catch((error) => {
        // oxlint-disable-next-line no-console -- debug logging
        console.log('Root: getProfileAsync failed with:', error)
        setIsLoading(false)
      })
  }, [])

  if (isLoading) {
    return <Loader />
  }

  return (
    <StrictMode>
      <MedplumProvider medplum={medplum} navigate={navigate}>
        <QueryProvider
          defaultQueryOptions={{
            staleTime: IS_DEV ? 0 : 1000 * 60 * 1, // off in dev mode, 1 minute in prod
          }}
        >
          <SeenApiContextProvider
            baseURL={SEEN_API_URL}
            medplum={medplum}
            appVersion={APP_VERSION}
            appCommitSha={APP_COMMIT_SHA}
          >
            <MantineProvider theme={SeenMantineTheme}>
              <ModalsProvider>
                <RouterProvider router={router} />
              </ModalsProvider>
            </MantineProvider>
          </SeenApiContextProvider>
        </QueryProvider>
      </MedplumProvider>
    </StrictMode>
  )
}
